import React, { useState } from 'react';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import useInspireOptions from '../../hooks/useInspireOptions';
import { Profile } from '../../Types';
import { cardTitle } from './styles';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import ExpandableCard from '../../commons/ExpandableCard';
import {
  CSAWeeklyHighlightsDisclaimer,
  ManagerWeeklyHighlightsDisclaimer,
} from './common/Alerts';
import CenteredLoader from '../../commons/CenteredLoader';
import CSASummariesTMView from './CSASummariesTMView';
import CSASummariesCSAView from './CSASummariesCSAView';
import { ViewContext } from './types';
import useHasPermissionToViewSummaries from './hooks/useHasPermissionToViewSummaries';
import {
  ClickstreamTypes,
  useClickstreamTags,
} from '../../hooks/useClickstreamTags';
import { CSA_SUMMARIES, CSA_SUMMARIES_SLOT } from './constants';
import useHasHadSummaries from './hooks/useHasHadSummaries';

interface CSASummariesContainerProps {
  profilesLoaded: boolean;
  displayedProfile: Profile;
  loggedInProfile: Profile;
  isProfileTm: boolean;
}

const CSASummariesContainer = ({
  displayedProfile,
  loggedInProfile,
  profilesLoaded,
  isProfileTm,
}: CSASummariesContainerProps) => {
  const intl = useIntl();
  const { isTm, requesterUsername } = useInspireOptions();
  const hasPermissionToViewSummaries = useHasPermissionToViewSummaries(
    displayedProfile,
    loggedInProfile
  );
  const viewContext =
    requesterUsername === displayedProfile.user.alias
      ? ViewContext.SELF
      : ViewContext.OTHER;

  const [disclaimerAlertVisible, setDisclaimerAlertVisible] = useState(true);

  const [expanderKey, setExpanderKey] = useState<string>();
  const [expanded, setExpanded] = useState(isTm);

  const { loading: hasHadSummariesLoading, hasHadSummary } = useHasHadSummaries(
    {
      alias: displayedProfile.user.alias,
      directReports: displayedProfile.directReports,
      isProfileTm,
      profilesLoaded,
    }
  );

  const handleAlertClose = () => {
    setExpanderKey(_.uniqueId());
  };

  const collapseClickstream = useClickstreamTags({
    type: ClickstreamTypes.Link,
    slotId: CSA_SUMMARIES_SLOT,
    contentId: 'summaries_container',
    feature: CSA_SUMMARIES,
    action: 'Collapse Expandable Card',
  });

  /*
   only show the container if the displayed profile is:
   - the logged in user
   - their manager
   - a proxy of their manager
   shared profiles do not have access to summaries.
   - they (or their direct reports) have had summaries in the past
  */
  if (!profilesLoaded || hasHadSummariesLoading)
    return <CenteredLoader data-testid='csa-summaries-loader' />;

  if (profilesLoaded && !hasPermissionToViewSummaries) return <></>;

  if (!hasHadSummary) return <></>;

  return (
    <Column data-testid='csa-summaries-container'>
      <Row width='100%' alignmentHorizontal='justify'>
        <Text type='h400'>
          {isProfileTm ? (
            <FormattedMessage
              id='cs_perfportal_team_performance'
              defaultMessage='Team Performance'
            />
          ) : (
            <FormattedMessage
              id='cs_perfportal_performance_insights'
              defaultMessage='Performance insights'
            />
          )}
        </Text>
      </Row>
      <ExpandableCard
        key={expanderKey} // workaround to force expander to update to new height on alert close
        expanded={expanded}
        onExpandedChange={setExpanded}
        titleRowSpacing='none none 400 none'
        cardSpacingInset='400 500 none 400'
        showDivider={false}
        showChevron={false}
        clickstreamTags={collapseClickstream}
        title={
          <Text type='h300'>
            <span className={cardTitle}>
              {isProfileTm
                ? intl.formatMessage({
                    id: 'cs_perfportal_my_teams_weekly_highlights',
                    defaultMessage: 'My team’s weekly highlights',
                  })
                : intl.formatMessage({
                    id: 'cs_perfportal_weekly_highlights',
                    defaultMessage: 'Weekly highlights',
                  })}
            </span>
          </Text>
        }
        expandedContent={
          <Box>
            {isTm ? (
              <>
                <ManagerWeeklyHighlightsDisclaimer
                  visible={disclaimerAlertVisible}
                  onClose={() => {
                    setDisclaimerAlertVisible(false);
                    handleAlertClose();
                  }}
                />
              </>
            ) : (
              <CSAWeeklyHighlightsDisclaimer
                visible={disclaimerAlertVisible}
                onClose={() => {
                  setDisclaimerAlertVisible(false);
                  handleAlertClose();
                }}
              />
            )}

            {!profilesLoaded ? (
              <CenteredLoader data-testid='csa-summaries-loader' />
            ) : isProfileTm ? (
              <CSASummariesTMView
                directReports={displayedProfile.directReports}
                viewContext={viewContext}
                data-testid='csa-summaries-tm-view'
              />
            ) : (
              <CSASummariesCSAView
                displayedProfile={displayedProfile}
                viewContext={viewContext}
                setParentExpanded={setExpanded}
              />
            )}
          </Box>
        }
      />
    </Column>
  );
};

export default CSASummariesContainer;
